import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useViewAs } from '../../context/ViewAsContext';
import { useRoleContext } from '../../hooks/useRoleContext';
import { LanguageSelector, useLanguage } from '../../context/LanguageContext';
import brand from '../../config/brand';
import { Button } from '../ui/button';
import NotificationBell from '../NotificationBell';
import {
  QA_VIEW_AS_WARNING_TEST_ID,
  resolveHeaderNavLinks,
} from '../../lib/roleExperience';
import { resolveProfileMenuLinks } from '../../lib/roleNavigationAudit';
import { isMarketingStudioEnabled } from '../../lib/marketingStudioAvailability';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Menu,
  X,
  User,
  LogOut,
  Home as HomeIcon,
  Building2,
  MapPin,
  LayoutDashboard,
  Settings,
  Bot,
  Heart,
  Calendar,
  Crown,
  Inbox,
  Eye,
  EyeOff,
  Megaphone,
} from 'lucide-react';

export default function Header() {
  const { user, logout, isAdmin } = useAuth();
  const { viewAsRole, setViewAsRole, clearViewAs } = useViewAs();
  const { showsOwnerNav, showsAdminNav, showsClientHeaderQuickLinks, canAccessClientDashboard, canAccessOwnerPricingLane, isAdmin: roleIsAdmin, viewAsRole: roleViewAsRole, user: roleUser } = useRoleContext();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // When admin uses view-as mode, adjust which nav items are shown.
  // Backend auth is unaffected — the real JWT still carries ADMIN role.
  const isViewingAs = isAdmin && !!viewAsRole;

  // Marketing Studio entry point: admins only, and only when the build-time
  // flag is on. showsAdminNav already excludes QA view-as mode.
  const showsMarketingStudioLink = showsAdminNav && isMarketingStudioEnabled();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const headerNavLinks = resolveHeaderNavLinks({
    showsClientHeaderQuickLinks: Boolean(user && showsClientHeaderQuickLinks),
  });

  const profileMenuLinks = user
    ? resolveProfileMenuLinks({
        user: roleUser,
        isAdmin: roleIsAdmin,
        viewAsRole: roleViewAsRole,
        showsClientHeaderQuickLinks,
        canAccessClientDashboard,
        canAccessOwnerPricingLane,
        showsOwnerNav,
        showsAdminNav,
      })
    : [];

  const profileMenuIcons = {
    'client-dashboard-link': LayoutDashboard,
    'owner-dashboard-link': LayoutDashboard,
    'owner-plans-link': Crown,
    'admin-link': Settings,
    'notifications-link': Inbox,
    'favorites-link': Heart,
    'bookings-link': Calendar,
    'settings-link': Settings,
    'pricing-link': Building2,
  };

  const navIconByHref = {
    '/listings': Building2,
    '/locations': MapPin,
    '/concierge': Bot,
    '/favorites': Heart,
    '/notifications': Inbox,
    '/dashboard': LayoutDashboard,
  };

  const isActive = (path) => location.pathname === path;
  const isDashboardActive = location.pathname === '/dashboard';

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-ivory/85 backdrop-blur-xl border-b border-champagne-200 shadow-[0_1px_24px_rgba(184,134,11,0.07)]" data-testid="header">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
            <div className="w-9 h-9 bg-gradient-to-br from-signal-blue to-military-gold rounded-xl shadow-md ring-1 ring-white/50 flex items-center justify-center transition-transform group-hover:scale-105">
              <HomeIcon className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-xl font-bold text-slate-900 tracking-tight hidden sm:block">
              {brand.brand_name_parts.prefix}{' '}
              <span className="text-military-gold">{brand.brand_name_parts.accent}</span>{' '}
              {brand.brand_name_parts.suffix}
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {headerNavLinks.map((link) => {
              const active = link.href === '/dashboard' ? isDashboardActive : isActive(link.href);
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
                    active
                      ? 'bg-slate-900 text-white shadow-md'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-champagne-100'
                  }`}
                  data-testid={link.testId}
                >
                  {t(link.labelKey, link.defaultLabel)}
                </Link>
              );
            })}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <LanguageSelector className="mr-2" />
            {user ? (
              <>
                {isViewingAs && (
                  <div className="flex items-center gap-2 max-w-xs">
                    <button
                      onClick={clearViewAs}
                      className="flex items-center gap-1.5 bg-amber-50 border border-amber-300 text-amber-700 text-xs font-medium px-2.5 py-1 rounded-lg hover:bg-amber-100 transition-colors shrink-0"
                      title={t('admin.return_to_admin', 'Return to Admin Mode')}
                      data-testid="view-as-badge"
                    >
                      <Eye className="w-3 h-3" />
                      <span>{t('admin.qa_viewing_as', 'QA:')} {viewAsRole}</span>
                      <X className="w-3 h-3 ml-0.5 opacity-60" />
                    </button>
                    <span
                      className="hidden xl:inline text-[10px] leading-snug text-amber-700"
                      data-testid={QA_VIEW_AS_WARNING_TEST_ID}
                    >
                      {t(
                        'admin.qa_data_warning',
                        'QA view changes the interface role only. Data still belongs to the signed-in account.',
                      )}
                    </span>
                  </div>
                )}
                <NotificationBell />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 text-slate-700 hover:text-slate-900" data-testid="user-menu-trigger">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isViewingAs ? 'bg-amber-100' : 'bg-slate-200'}`}>
                        <User className={`w-4 h-4 ${isViewingAs ? 'text-amber-600' : 'text-slate-600'}`} />
                      </div>
                      <span className="text-sm">{user.name}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-white border-slate-200 shadow-lg">
                    {isViewingAs && (
                      <>
                        <div className="px-2 py-2 bg-amber-50 border-b border-amber-200">
                          <p className="text-xs font-semibold text-amber-800">{t('admin.qa_mode_label', 'QA Preview')}</p>
                          <p className="text-xs text-amber-600">{t('admin.qa_viewing_as', 'QA:')} {viewAsRole}</p>
                          <p className="text-[10px] text-amber-700 mt-1" data-testid={`${QA_VIEW_AS_WARNING_TEST_ID}-menu`}>
                            {t(
                              'admin.qa_data_warning',
                              'QA view changes the interface role only. Data still belongs to the signed-in account.',
                            )}
                          </p>
                        </div>
                      </>
                    )}
                    <div className="px-2 py-1.5">
                      <p className="text-sm text-slate-700">{user.email}</p>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">{user.role}</p>
                    </div>
                    <DropdownMenuSeparator className="bg-slate-200" />
                    {profileMenuLinks.map((item) => {
                      const Icon = profileMenuIcons[item.testId] || Settings;
                      return (
                        <DropdownMenuItem
                          key={item.testId}
                          onClick={() => navigate(item.href)}
                          className="text-slate-700 hover:text-slate-900 hover:bg-slate-100 cursor-pointer"
                          data-testid={item.testId}
                        >
                          <Icon className="w-4 h-4 mr-2" />
                          {t(item.labelKey, item.defaultLabel)}
                        </DropdownMenuItem>
                      );
                    })}
                    {showsMarketingStudioLink && (
                      <DropdownMenuItem
                        onClick={() => navigate('/admin/marketing')}
                        className="text-slate-700 hover:text-slate-900 hover:bg-slate-100 cursor-pointer"
                        data-testid="marketing-studio-link"
                      >
                        <Megaphone className="w-4 h-4 mr-2" />
                        {t('marketingStudio.title')}
                      </DropdownMenuItem>
                    )}
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator className="bg-slate-200" />
                        {!viewAsRole ? (
                          <>
                            <div className="px-2 py-1 text-xs text-slate-400 uppercase tracking-wider font-medium">
                              {t('admin.qa_mode_label', 'QA Preview')}
                            </div>
                            <DropdownMenuItem onClick={() => setViewAsRole('OWNER')} className="text-slate-700 hover:text-slate-900 hover:bg-slate-100 cursor-pointer" data-testid="view-as-owner-btn">
                              <Eye className="w-4 h-4 mr-2" />
                              {t('admin.view_as_owner', 'View as Owner')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setViewAsRole('USER')} className="text-slate-700 hover:text-slate-900 hover:bg-slate-100 cursor-pointer" data-testid="view-as-user-btn">
                              <Eye className="w-4 h-4 mr-2" />
                              {t('admin.view_as_user', 'View as Guest')}
                            </DropdownMenuItem>
                          </>
                        ) : (
                          <DropdownMenuItem onClick={clearViewAs} className="text-amber-700 hover:text-amber-800 hover:bg-amber-50 cursor-pointer" data-testid="return-to-admin-btn">
                            <EyeOff className="w-4 h-4 mr-2" />
                            {t('admin.return_to_admin', 'Return to Admin Mode')}
                          </DropdownMenuItem>
                        )}
                      </>
                    )}
                    <DropdownMenuSeparator className="bg-slate-200" />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600 hover:text-red-700 hover:bg-red-50 cursor-pointer" data-testid="logout-btn">
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('nav.logout', 'Logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/login')} className="text-slate-700 hover:text-slate-900 hover:bg-champagne-100 rounded-full px-4" data-testid="login-btn">
                  {t('nav.login', 'Sign In')}
                </Button>
                <Button onClick={() => navigate('/register')} className="rounded-full px-5 bg-gradient-to-r from-military-gold to-amber-500 hover:from-amber-600 hover:to-amber-500 text-white shadow-md hover:shadow-lg transition-all" data-testid="register-btn">
                  {t('nav.register', 'Get Started')}
                </Button>
              </>
            )}
          </div>

          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-slate-600 hover:text-slate-900" data-testid="mobile-menu-toggle">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-slate-200 shadow-lg">
          <nav className="px-4 py-4 space-y-2">
            {headerNavLinks.map((link) => {
              const Icon = navIconByHref[link.href] || Building2;
              const active = link.href === '/dashboard' ? isDashboardActive : isActive(link.href);
              return (
                <Link
                  key={`mobile-${link.href}`}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg ${
                    active
                      ? 'text-signal-blue bg-slate-100'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                  data-testid={`mobile-${link.testId}`}
                >
                  <Icon className="w-4 h-4" />
                  {t(link.labelKey, link.defaultLabel)}
                </Link>
              );
            })}
            <div className="border-t border-slate-200 pt-4 mt-4">
              {user ? (
                <>
                  <div className="px-3 py-2 text-sm text-slate-600">
                    {user.name} • {isViewingAs ? `${user.role} → ${viewAsRole}` : user.role}
                  </div>
                  {isViewingAs && (
                    <>
                      <button
                        onClick={() => { clearViewAs(); setMobileMenuOpen(false); }}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-amber-700 bg-amber-50 w-full rounded-lg mb-1"
                        data-testid="mobile-return-to-admin-btn"
                      >
                        <EyeOff className="w-4 h-4" />
                        {t('admin.return_to_admin', 'Return to Admin Mode')}
                      </button>
                      <p className="px-3 pb-2 text-[10px] text-amber-700" data-testid={`mobile-${QA_VIEW_AS_WARNING_TEST_ID}`}>
                        {t(
                          'admin.qa_data_warning',
                          'QA view changes the interface role only. Data still belongs to the signed-in account.',
                        )}
                      </p>
                    </>
                  )}
                  {profileMenuLinks.map((item) => {
                    const Icon = profileMenuIcons[item.testId] || Settings;
                    return (
                      <Link
                        key={`mobile-${item.testId}`}
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-slate-900"
                        data-testid={`mobile-${item.testId}`}
                      >
                        <Icon className="w-4 h-4" />
                        {t(item.labelKey, item.defaultLabel)}
                      </Link>
                    );
                  })}
                  {showsMarketingStudioLink && (
                    <Link
                      to="/admin/marketing"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-slate-900"
                      data-testid="mobile-marketing-studio-link"
                    >
                      <Megaphone className="w-4 h-4" />
                      {t('marketingStudio.title')}
                    </Link>
                  )}
                  {isAdmin && !viewAsRole && (
                    <>
                      <div className="border-t border-slate-100 pt-2 mt-2">
                        <button onClick={() => { setViewAsRole('OWNER'); setMobileMenuOpen(false); }} className="flex items-center gap-2 px-3 py-2 text-sm text-slate-500 hover:text-slate-700 w-full">
                          <Eye className="w-4 h-4" />
                          {t('admin.view_as_owner', 'View as Owner')}
                        </button>
                        <button onClick={() => { setViewAsRole('USER'); setMobileMenuOpen(false); }} className="flex items-center gap-2 px-3 py-2 text-sm text-slate-500 hover:text-slate-700 w-full">
                          <Eye className="w-4 h-4" />
                          {t('admin.view_as_user', 'View as Guest')}
                        </button>
                      </div>
                    </>
                  )}
                  <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 w-full">
                    <LogOut className="w-4 h-4" />
                    {t('nav.logout', 'Logout')}
                  </button>
                </>
              ) : (
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => { navigate('/login'); setMobileMenuOpen(false); }} className="flex-1 text-slate-600">
                    {t('nav.login', 'Sign In')}
                  </Button>
                  <Button onClick={() => { navigate('/register'); setMobileMenuOpen(false); }} className="flex-1 bg-signal-blue text-white">
                    {t('nav.register', 'Get Started')}
                  </Button>
                </div>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
